import React, { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Field } from "../components/Field";
import { formatDateTime, toDateTimeInput } from "../lib/format";

export default function AutoEndReviewPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: ["carpintel_session", id],
    queryFn: () => fetch(`/app-api/sessions/${id}`).then((r) => {
      if (!r.ok) throw new Error("Session not found");
      return r.json();
    }),
  });
  const session = sessionQuery.data;
  const initialEnd = useMemo(() => session?.end_at ? toDateTimeInput(session.end_at) : "", [session?.end_at]);
  const [endAt, setEndAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const value = endAt || initialEnd;

  async function confirm() {
    if (!session || !value) return;
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/auto-end-review`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ end_at: new Date(value).toISOString() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not confirm session finish");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] }),
      ]);
      navigate(`/sessions/${id}`, { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not confirm session finish");
    } finally { setSaving(false); }
  }

  if (sessionQuery.isLoading) return <div className="mx-auto max-w-[760px] px-4 py-8 text-sm text-secondary">Loading session review…</div>;
  if (sessionQuery.error || !session) return <div className="mx-auto max-w-[760px] px-4 py-8 text-sm text-secondary">That session could not be found.</div>;
  if (!session.auto_ended || session.auto_end_reviewed_at) return <Navigate to={`/sessions/${id}`} replace />;

  return (
    <div className="mx-auto max-w-[760px] space-y-5 px-4 py-6 pb-28">
      <header><Link to="/app" className="text-sm font-medium text-accent">← Home</Link><p className="mt-3 text-xs font-semibold uppercase tracking-wide text-accent">Quick review</p><h1 className="mt-1 text-2xl font-semibold text-primary">CatchCompass closed this session for you</h1><p className="mt-2 text-sm leading-6 text-secondary">It passed the planned finish and stayed open for the one-hour grace period. Confirm the finish below, or correct it if you actually packed up earlier.</p></header>
      <section className="rounded-xl border border-border bg-raised p-4"><p className="font-semibold text-primary">{session.lake_name}{session.peg ? ` · ${session.peg}` : ""}</p><p className="mt-1 text-sm text-secondary">Started {formatDateTime(session.start_at)}</p><p className="mt-1 text-sm text-secondary">Automatically closed {formatDateTime(session.end_at)}</p></section>
      <section className="space-y-3 rounded-xl border border-border bg-raised p-4"><Field label="Actual finish" id="review-end"><Input id="review-end" type="datetime-local" value={value} onChange={(e) => setEndAt(e.target.value)} /></Field><p className="text-xs leading-5 text-secondary">Confirming keeps the session evidence but marks this automatic close as reviewed. You can still add missing catch weights from the session afterwards.</p><Button className="w-full" onClick={confirm} isLoading={saving}>Confirm session finish</Button></section>
      {message ? <p className="rounded-lg border border-error bg-error-weak p-3 text-sm text-error">{message}</p> : null}
    </div>
  );
}
